import type { ReactNode } from "react";
import { cn } from "@/utils/cn";
import { iconToneBg, type IconTone } from "./iconTones";

/**
 * One row in a RequestInfoGrid.
 *
 * Set `hidden: true` for conditional rows instead of wrapping in JSX conditionals —
 * this keeps the items array declarative and avoids key instability.
 *
 * Use `iconBg` to override the tone background when the exact color isn't in the
 * predefined tone map (e.g. `"bg-warning"`).
 */
export interface RequestInfoItem {
  key: string;
  icon: ReactNode;
  tone: IconTone;
  /** Override the background class when the color doesn't fit a predefined tone. */
  iconBg?: string;
  label: string;
  value: ReactNode;
  fullWidth?: boolean;
  hidden?: boolean;
}

interface Props {
  items: RequestInfoItem[];
  className?: string;
}

export const RequestInfoGrid = ({ items, className }: Props) => (
  <section
    className={cn("grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-8", className)}
  >
    {items
      .filter((item) => !item.hidden)
      .map((item) => (
        <div
          key={item.key}
          className={cn(
            "flex items-center gap-3",
            item.fullWidth && "sm:col-span-2",
          )}
        >
          <div
            className={cn(
              "p-3 rounded-xs w-fit shrink-0",
              item.iconBg ?? iconToneBg[item.tone],
            )}
          >
            {item.icon}
          </div>
          <div className="min-w-0">
            <p className="text-gray-500 text-xs font-medium">{item.label}</p>
            <div className="text-sm font-medium wrap-break-word">
              {item.value}
            </div>
          </div>
        </div>
      ))}
  </section>
);
